import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link } from 'react-router-dom';
import { useComplianceMatrix } from '../../../../hooks/useComplianceMatrix';
import { useProject } from '../../../../hooks/useProjects';
import { SectionAssignments } from './SectionAssignments';
import { flattenSections, useOutlineSections } from './workingOutlineHooks';
export function SectionMappingContext({ projectId, volumeId, sectionId }) {
    const outline = useOutlineSections(projectId, volumeId);
    const matrix = useComplianceMatrix(projectId);
    const project = useProject(projectId);
    const members = project.data?.members ?? [];
    const memberName = (id)=>members.find((member)=>member.userId === id)?.name || members.find((member)=>member.userId === id)?.email || id || 'unassigned — ownership gap';
    const today = new Date();
    const calendarToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const sections = flattenSections(outline.data ?? []).filter((section)=>!sectionId || section.sectionId === sectionId);
    return /*#__PURE__*/ _jsxs("aside", {
        "aria-label": "Affected requirement mappings",
        className: "space-y-2 border-b border-border bg-card p-3 text-foreground",
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "font-semibold",
                children: "Affected requirement mappings"
            }),
            outline.isLoading ? /*#__PURE__*/ _jsx("output", {
                children: "Loading mappings…"
            }) : outline.isError ? /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Unable to load sections.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "link",
                        size: "sm",
                        onClick: ()=>outline.refetch(),
                        children: "Retry sections"
                    })
                ]
            }) : sections.length === 0 ? /*#__PURE__*/ _jsx("p", {
                children: "No sections yet. Add a section to begin writing without mappings."
            }) : sections.map((section)=>{
                const requirements = (matrix.data?.requirements ?? []).filter((requirement)=>section.requirementIds.includes(requirement.requirementId) || requirement.mapping?.proposalSection === section.sectionId);
                return /*#__PURE__*/ _jsxs("details", {
                    open: !!sectionId,
                    children: [
                        /*#__PURE__*/ _jsxs("summary", {
                            children: [
                                section.title,
                                " · ",
                                requirements.length,
                                " mapped requirements"
                            ]
                        }),
                        project.isLoading ? /*#__PURE__*/ _jsx("output", {
                            children: "Loading project members…"
                        }) : project.isError ? /*#__PURE__*/ _jsxs("div", {
                            role: "alert",
                            children: [
                                "Unable to load project members.",
                                ' ',
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "button",
                                    variant: "link",
                                    size: "sm",
                                    onClick: ()=>project.refetch(),
                                    children: "Retry project members"
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(SectionAssignments, {
                            projectId: projectId,
                            section: section,
                            members: members,
                            canEdit: project.data?.role === 'owner' || project.data?.role === 'member'
                        }, section.sectionId),
                        !section.writerUserId && /*#__PURE__*/ _jsx("p", {
                            "data-testid": Testids.proposalAssignments.unassigned,
                            children: "Unassigned section — writing gap"
                        }),
                        section.internalDate && section.internalDate < calendarToday && /*#__PURE__*/ _jsxs("p", {
                            "data-testid": Testids.proposalAssignments.overdue,
                            children: [
                                "Overdue internal date:",
                                ' ',
                                /*#__PURE__*/ _jsx("time", {
                                    dateTime: section.internalDate,
                                    children: section.internalDate
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            children: "Requirement ownership and section writing are separate assignments."
                        }),
                        matrix.isLoading && /*#__PURE__*/ _jsx("output", {
                            children: "Loading requirement mappings…"
                        }),
                        matrix.isError && /*#__PURE__*/ _jsxs("div", {
                            role: "alert",
                            children: [
                                "Unable to load requirement mappings.",
                                ' ',
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "button",
                                    variant: "link",
                                    size: "sm",
                                    onClick: ()=>matrix.refetch(),
                                    children: "Retry mappings"
                                })
                            ]
                        }),
                        !matrix.isLoading && !matrix.isError && requirements.length === 0 && /*#__PURE__*/ _jsx("p", {
                            children: "Mapping gap: no requirements linked yet. Writing can begin."
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            children: requirements.map((requirement)=>/*#__PURE__*/ _jsxs("li", {
                                    children: [
                                        /*#__PURE__*/ _jsxs(Link, {
                                            to: `/proposal/${projectId}/v2/compliance`,
                                            children: [
                                                requirement.requirementId,
                                                ": ",
                                                requirement.source.exactText
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Requirement owner: ",
                                                memberName(requirement.assignedToUserId)
                                            ]
                                        }),
                                        requirement.assignedToUserId && section.writerUserId && requirement.assignedToUserId !== section.writerUserId && /*#__PURE__*/ _jsxs("p", {
                                            "data-testid": Testids.proposalAssignments.mismatch,
                                            children: [
                                                "Owner/writer mismatch: requirement owner",
                                                ' ',
                                                memberName(requirement.assignedToUserId),
                                                "; section writer",
                                                ' ',
                                                memberName(section.writerUserId),
                                                "."
                                            ]
                                        }),
                                        requirement.source.filename && /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Source: ",
                                                requirement.source.filename
                                            ]
                                        })
                                    ]
                                }, requirement.requirementId))
                        }),
                        section.taggedFileIds.length > 0 && /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Linked evidence: ",
                                section.taggedFileIds.join(', '),
                                ".",
                                ' ',
                                /*#__PURE__*/ _jsx(Link, {
                                    to: `/proposal/${projectId}/v2/sources/files`,
                                    children: "Open source files"
                                })
                            ]
                        })
                    ]
                }, section.sectionId);
            })
        ]
    });
}
